// opsRequests — shared types, labels, queries and mutations for Ops Requests
// (Sep 25, 2026): the board where sales reps leave the Roof Team Manager
// job-tied tasks and he works them New → In progress → Waiting → Done.
//
// One source of truth so the Requests page, the deal-page card and the home
// widget can never drift on labels, sort order or aging rules.
//
// Permission model lives in the DATABASE (RLS + the ops_requests_guard
// trigger); the helpers at the bottom only decide what to RENDER. Every write
// here is a plain table write — the guard trigger owns the lifecycle stamps
// (started_at / done_at / …) and rejects anything the caller may not do.

import { supabase } from "./supabase";
import { fmtSupabaseError } from "./fmtError";
import {
  prepareAttachment,
  isImageMime,
  type AttachmentFailure,
  type UploadedAttachment,
} from "./warrantyAttachments";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export type OpsCategory =
  | "pre_inspection" | "adjuster_meeting" | "reinspection" | "build"
  | "materials" | "repair" | "warranty" | "other";
export type OpsPriority = "normal" | "urgent";
export type OpsStatus = "new" | "in_progress" | "waiting" | "done" | "withdrawn";
export type OpsWaitingOn = "homeowner" | "carrier" | "rep" | "other";

export interface OpsRequest {
  id: string;
  deal_id: string;
  requested_by: string;
  assigned_to: string | null;
  category: OpsCategory;
  priority: OpsPriority;
  body: string;
  needed_by: string | null;          // date (YYYY-MM-DD)
  status: OpsStatus;
  waiting_on: OpsWaitingOn | null;
  waiting_note: string | null;
  done_note: string | null;
  withdraw_reason: string | null;
  homeowner_name: string | null;
  property_address: string | null;
  deal_stage: string | null;
  deal_owner_id: string | null;
  deal_split_rm_id: string | null;
  started_at: string | null;
  waiting_at: string | null;
  done_at: string | null;
  done_by: string | null;
  withdrawn_at: string | null;
  reassigned_at: string | null;
  reassigned_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface OpsRequestEvent {
  id: string;
  request_id: string;
  author_id: string | null;
  kind: "comment" | "status" | "reassign";
  body: string | null;
  payload: Record<string, unknown>;
  created_at: string;
}

export interface OpsAttachment {
  id: string;
  file_name: string;
  file_type: string;
  storage_path: string;
  mime_type: string | null;
  file_size: number | null;
  created_at: string;
}

export interface RosterLite {
  id: string;
  full_name: string;
  role: string;
  team_lead_id?: string | null;
  is_active?: boolean;
}

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------
export const CATEGORIES: Array<{ key: OpsCategory; label: string; icon: string }> = [
  { key: "pre_inspection",   label: "Pre-inspection",      icon: "🧗" },
  { key: "adjuster_meeting", label: "Adjuster meeting",    icon: "🏠" },
  { key: "reinspection",     label: "Reinspection",        icon: "🔁" },
  { key: "build",            label: "Build / schedule",    icon: "🔨" },
  { key: "materials",        label: "Materials",           icon: "📦" },
  { key: "repair",           label: "Repair / punch list", icon: "🔧" },
  { key: "warranty",         label: "Warranty",            icon: "🛡" },
  { key: "other",            label: "Other",               icon: "📝" },
];
export const CATEGORY_LABEL: Record<OpsCategory, string> =
  Object.fromEntries(CATEGORIES.map((c) => [c.key, c.label])) as Record<OpsCategory, string>;
export const CATEGORY_ICON: Record<OpsCategory, string> =
  Object.fromEntries(CATEGORIES.map((c) => [c.key, c.icon])) as Record<OpsCategory, string>;

export const STATUS_LABEL: Record<OpsStatus, string> = {
  new: "New",
  in_progress: "In progress",
  waiting: "Waiting",
  done: "Done",
  withdrawn: "Withdrawn",
};
export const STATUS_CLASS: Record<OpsStatus, string> = {
  new: "bg-orange-50 text-orange-700 border border-orange-200",
  in_progress: "bg-violet-50 text-violet-700 border border-violet-200",
  waiting: "bg-amber-50 text-amber-800 border border-amber-200",
  done: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  withdrawn: "bg-slate-100 text-slate-500 border border-slate-200",
};
export const WAITING_ON_LABEL: Record<OpsWaitingOn, string> = {
  homeowner: "homeowner",
  carrier: "carrier",
  rep: "the rep",
  other: "other",
};
export const WAITING_ON_OPTIONS: OpsWaitingOn[] = ["homeowner", "carrier", "rep", "other"];

export const OPEN_STATUSES: OpsStatus[] = ["new", "in_progress", "waiting"];
export function isOpen(r: Pick<OpsRequest, "status">): boolean {
  return OPEN_STATUSES.includes(r.status);
}

/** deal_documents.file_type values for request attachments. */
export const OPS_FILE_TYPES = { photo: "ops_request_photo", document: "ops_request_document" } as const;
export const OPS_ATTACHMENT_FILE_TYPES: string[] = [OPS_FILE_TYPES.photo, OPS_FILE_TYPES.document];

// ---------------------------------------------------------------------------
// Aging + ordering (the list does the nagging)
// ---------------------------------------------------------------------------
const DAY_MS = 86_400_000;
export const AGING_AMBER_DAYS = 3;
export const AGING_RED_DAYS = 7;

export function daysOpen(r: Pick<OpsRequest, "created_at">, now = Date.now()): number {
  return Math.max(0, Math.floor((now - new Date(r.created_at).getTime()) / DAY_MS));
}

/** Local-date compare; needed_by is a DATE column, so never `new Date("YYYY-MM-DD")` directly. */
export function isPastNeededBy(r: Pick<OpsRequest, "needed_by">, now = new Date()): boolean {
  if (!r.needed_by) return false;
  const d = new Date(`${r.needed_by}T12:00:00`);
  const today = new Date(now); today.setHours(0, 0, 0, 0);
  return d.getTime() < today.getTime();
}

export type AgingTone = "none" | "amber" | "red";
export function agingTone(r: Pick<OpsRequest, "status" | "created_at" | "needed_by">, now = Date.now()): AgingTone {
  if (!isOpen(r)) return "none";
  const days = daysOpen(r, now);
  if (isPastNeededBy(r, new Date(now)) || days >= AGING_RED_DAYS) return "red";
  if (days >= AGING_AMBER_DAYS) return "amber";
  return "none";
}

export function agingLabel(r: Pick<OpsRequest, "status" | "created_at" | "needed_by">, now = Date.now()): string {
  const d = daysOpen(r, now);
  if (isOpen(r) && isPastNeededBy(r, new Date(now))) return `past needed-by · ${d}d open`;
  return d === 0 ? "today" : `${d}d open`;
}

export function fmtNeededBy(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

export function fmtWhen(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleString("en-US", {
    month: "short", day: "numeric", ...(sameYear ? {} : { year: "numeric" }),
    hour: "numeric", minute: "2-digit",
  });
}

/** Queue order: urgent → needed-by (soonest, nulls last) → oldest first. */
export function sortForQueue<T extends Pick<OpsRequest, "priority" | "needed_by" | "created_at">>(rows: T[]): T[] {
  return [...rows].sort((a, b) => {
    if (a.priority !== b.priority) return a.priority === "urgent" ? -1 : 1;
    if (a.needed_by !== b.needed_by) {
      if (!a.needed_by) return 1;
      if (!b.needed_by) return -1;
      return a.needed_by < b.needed_by ? -1 : 1;
    }
    return a.created_at < b.created_at ? -1 : a.created_at > b.created_at ? 1 : 0;
  });
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------
const CLOSED_WINDOW_DAYS = 60;

/**
 * Every open request the caller may see, plus closed ones (done / withdrawn)
 * from the last 60 days so the Done tab and the leadership stats have data.
 * RLS is the scope; the page only filters further for display.
 */
export async function fetchRequests(): Promise<OpsRequest[]> {
  const since = new Date(Date.now() - CLOSED_WINDOW_DAYS * DAY_MS).toISOString();
  const { data, error } = await supabase
    .from("ops_requests")
    .select("*")
    .or(`status.in.(new,in_progress,waiting),updated_at.gte.${since}`)
    .order("created_at", { ascending: false })
    .limit(2000);
  if (error) throw new Error(fmtSupabaseError(error));
  return (data ?? []) as OpsRequest[];
}

export async function fetchRequestsForDeal(dealId: string): Promise<OpsRequest[]> {
  const { data, error } = await supabase
    .from("ops_requests")
    .select("*")
    .eq("deal_id", dealId)
    .order("created_at", { ascending: false });
  if (error) throw new Error(fmtSupabaseError(error));
  return (data ?? []) as OpsRequest[];
}

export async function fetchRequest(id: string): Promise<OpsRequest | null> {
  const { data, error } = await supabase.from("ops_requests").select("*").eq("id", id).maybeSingle();
  if (error) throw new Error(fmtSupabaseError(error));
  return (data as OpsRequest | null) ?? null;
}

export async function fetchEvents(requestId: string): Promise<OpsRequestEvent[]> {
  const { data, error } = await supabase
    .from("ops_request_events")
    .select("*")
    .eq("request_id", requestId)
    .order("created_at", { ascending: true });
  if (error) throw new Error(fmtSupabaseError(error));
  return (data ?? []) as OpsRequestEvent[];
}

export async function fetchAttachments(requestId: string): Promise<OpsAttachment[]> {
  const { data, error } = await supabase
    .from("deal_documents")
    .select("id, file_name, file_type, storage_path, mime_type, file_size, created_at")
    .eq("ops_request_id", requestId)
    .order("created_at", { ascending: true });
  if (error) throw new Error(fmtSupabaseError(error));
  return (data ?? []) as OpsAttachment[];
}

/** Counts of OPEN dispatch questions per deal — the "same job has an open question" chip. */
export async function fetchOpenDispatchQuestionDeals(dealIds: string[]): Promise<Set<string>> {
  const ids = [...new Set(dealIds.filter(Boolean))];
  if (!ids.length) return new Set();
  const { data, error } = await supabase
    .from("dispatch_questions")
    .select("deal_id")
    .in("deal_id", ids)
    .eq("status", "open");
  if (error) return new Set(); // best-effort chip; never blocks the board
  return new Set((data ?? []).map((r) => r.deal_id as string));
}

export async function fetchRoster(): Promise<RosterLite[]> {
  const { data, error } = await supabase
    .from("team_roster")
    .select("id, full_name, role, team_lead_id, is_active");
  if (error) throw new Error(fmtSupabaseError(error));
  return (data ?? []) as RosterLite[];
}

export interface DealSearchHit {
  id: string;
  homeowner_name: string | null;
  property_street: string | null;
  property_city: string | null;
  stage: string | null;
  owner_id: string | null;
  split_rm_id: string | null;
}

/** Job typeahead for the Requests-page "New request" flow. RLS scopes what comes back. */
export async function searchDeals(q: string): Promise<DealSearchHit[]> {
  // Commas and parens break a PostgREST .or() filter; wildcards would widen it.
  const needle = q.replace(/[,()%_]/g, " ").trim();
  if (needle.length < 2) return [];
  const { data, error } = await supabase
    .from("deals")
    .select("id, homeowner_name, property_street, property_city, stage, owner_id, split_rm_id")
    .or(`homeowner_name.ilike.%${needle}%,property_street.ilike.%${needle}%`)
    .not("stage", "in", "(closed_lost,cancelled)")
    .order("created_at", { ascending: false })
    .limit(10);
  if (error) throw new Error(fmtSupabaseError(error));
  return (data ?? []) as DealSearchHit[];
}

// ---------------------------------------------------------------------------
// Writes (the guard trigger enforces who may do what)
// ---------------------------------------------------------------------------
export interface FileRequestInput {
  dealId: string;
  requestedBy: string;
  category: OpsCategory;
  priority: OpsPriority;
  body: string;
  neededBy: string | null;
}

export async function fileRequest(input: FileRequestInput): Promise<OpsRequest> {
  const { data, error } = await supabase
    .from("ops_requests")
    .insert({
      deal_id: input.dealId,
      requested_by: input.requestedBy,
      category: input.category,
      priority: input.priority,
      body: input.body.trim(),
      needed_by: input.neededBy || null,
    })
    .select("*")
    .single();
  if (error) throw new Error(fmtSupabaseError(error));
  return data as OpsRequest;
}

async function patch(id: string, values: Partial<OpsRequest>): Promise<OpsRequest> {
  const { data, error } = await supabase.from("ops_requests").update(values).eq("id", id).select("*").single();
  if (error) throw new Error(fmtSupabaseError(error));
  return data as OpsRequest;
}

export const startRequest = (id: string) => patch(id, { status: "in_progress" });
export const resumeRequest = (id: string) => patch(id, { status: "in_progress" });
export const reopenRequest = (id: string) => patch(id, { status: "new" });
export const markWaiting = (id: string, waitingOn: OpsWaitingOn, note: string) =>
  patch(id, { status: "waiting", waiting_on: waitingOn, waiting_note: note.trim() || null });
export const markDone = (id: string, note: string) =>
  patch(id, { status: "done", done_note: note.trim() || null });
export const withdrawRequest = (id: string, reason: string) =>
  patch(id, { status: "withdrawn", withdraw_reason: reason.trim() });
export const reassignRequest = (id: string, toMemberId: string) =>
  patch(id, { assigned_to: toMemberId });
export const editRequest = (
  id: string,
  values: { category: OpsCategory; priority: OpsPriority; body: string; needed_by: string | null },
) => patch(id, { ...values, body: values.body.trim(), needed_by: values.needed_by || null });

export async function addComment(requestId: string, authorId: string, body: string): Promise<void> {
  const text = body.trim();
  if (!text) return;
  const { error } = await supabase
    .from("ops_request_events")
    .insert({ request_id: requestId, author_id: authorId, kind: "comment", body: text });
  if (error) throw new Error(fmtSupabaseError(error));
}

// ---------------------------------------------------------------------------
// Attachments — same upload path as warranty claims, tagged to the request.
// ---------------------------------------------------------------------------
export interface OpsUploadResult {
  uploaded: UploadedAttachment[];
  failures: AttachmentFailure[];
}

export async function uploadRequestAttachments(args: {
  dealId: string;
  requestId: string;
  files: File[];
  uploaderId: string | null | undefined;
  onProgress?: (done: number, total: number) => void;
}): Promise<OpsUploadResult> {
  const { dealId, requestId, files, uploaderId, onProgress } = args;
  const result: OpsUploadResult = { uploaded: [], failures: [] };
  let done = 0;
  for (const picked of files) {
    const prep = await prepareAttachment(picked);
    if (!prep.ok) {
      result.failures.push(prep.failure);
      onProgress?.(++done, files.length);
      continue;
    }
    const { file, mime, ext } = prep.prepared;
    const fileType = isImageMime(mime) ? OPS_FILE_TYPES.photo : OPS_FILE_TYPES.document;
    const path = `ops-requests/${dealId}/${requestId}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;

    const { error: uploadErr } = await supabase.storage
      .from("deal-documents")
      .upload(path, file, { contentType: mime, upsert: false });
    if (uploadErr) {
      result.failures.push({ name: picked.name, reason: fmtSupabaseError(uploadErr) });
      onProgress?.(++done, files.length);
      continue;
    }
    const { error: docErr } = await supabase.from("deal_documents").insert({
      deal_id: dealId,
      file_name: file.name,
      file_type: fileType,
      storage_path: path,
      file_size: file.size,
      mime_type: mime,
      uploaded_by: uploaderId ?? null,
      ops_request_id: requestId,
    });
    if (docErr) {
      await supabase.storage.from("deal-documents").remove([path]).then(() => undefined, () => undefined);
      result.failures.push({ name: picked.name, reason: fmtSupabaseError(docErr) });
      onProgress?.(++done, files.length);
      continue;
    }
    result.uploaded.push({ storage_path: path, file_name: file.name, mime_type: mime, file_size: file.size, file_type: fileType });
    onProgress?.(++done, files.length);
  }
  return result;
}

export async function signedUrl(storagePath: string): Promise<string | null> {
  const { data, error } = await supabase.storage.from("deal-documents").createSignedUrl(storagePath, 3600);
  if (error) return null;
  return data?.signedUrl ?? null;
}

// ---------------------------------------------------------------------------
// Client-side render permissions (mirror of the DB rules — the DB decides)
// ---------------------------------------------------------------------------
const LEADERSHIP = new Set(["admin", "sales_director", "director_of_operations", "roof_director"]);
const FILERS = new Set(["restore_manager", "team_lead", "asm", ...LEADERSHIP]);
const SEE_ALL = new Set(["asm", "roof_team_manager", ...LEADERSHIP]);

export function isLeadership(role: string | null | undefined, isSuperUser: boolean): boolean {
  return isSuperUser || LEADERSHIP.has(role ?? "");
}
export function canFileRequests(role: string | null | undefined, isSuperUser: boolean): boolean {
  return isSuperUser || FILERS.has(role ?? "");
}
export function canSeeAllRequests(role: string | null | undefined, isSuperUser: boolean): boolean {
  return isSuperUser || SEE_ALL.has(role ?? "");
}
/** Who may open the board at all (mirrors the route allowlist). */
export function canOpenRequestsBoard(role: string | null | undefined, isSuperUser: boolean): boolean {
  return isSuperUser || FILERS.has(role ?? "") || SEE_ALL.has(role ?? "");
}

export interface ActorContext {
  memberId: string | null;
  role: string | null | undefined;
  isSuperUser: boolean;
}

export function isAssignee(r: OpsRequest, a: ActorContext): boolean {
  return !!a.memberId && r.assigned_to === a.memberId;
}
export function isRequester(r: OpsRequest, a: ActorContext): boolean {
  return !!a.memberId && r.requested_by === a.memberId;
}
export function canWork(r: OpsRequest, a: ActorContext): boolean {
  return isAssignee(r, a) || isLeadership(a.role, a.isSuperUser);
}
export function canWithdraw(r: OpsRequest, a: ActorContext): boolean {
  return r.status !== "done" && r.status !== "withdrawn" && (isRequester(r, a) || isLeadership(a.role, a.isSuperUser));
}
export function canEditFields(r: OpsRequest, a: ActorContext): boolean {
  if (canWork(r, a)) return isOpen(r);
  return isRequester(r, a) && r.status === "new";
}
export function canReassign(a: ActorContext): boolean {
  return isLeadership(a.role, a.isSuperUser);
}

// ---------------------------------------------------------------------------
// Leadership stats
// ---------------------------------------------------------------------------
export interface OpsStats {
  open: number;
  urgent: number;
  overdue: number;
  oldestDays: number | null;
  filed30: number;
  closed30: number;
  avgDaysToClose30: number | null;
  perRep: Array<{ id: string; count: number }>;
  perCategory: Array<{ key: OpsCategory; count: number }>;
}

export function computeStats(rows: OpsRequest[], now = Date.now()): OpsStats {
  const open = rows.filter(isOpen);
  const since30 = now - 30 * DAY_MS;
  const filed30 = rows.filter((r) => new Date(r.created_at).getTime() >= since30);
  const closed30 = rows.filter((r) => r.status === "done" && r.done_at && new Date(r.done_at).getTime() >= since30);
  const avg = closed30.length
    ? closed30.reduce((s, r) => s + (new Date(r.done_at as string).getTime() - new Date(r.created_at).getTime()) / DAY_MS, 0) / closed30.length
    : null;
  const perRepMap = new Map<string, number>();
  for (const r of filed30) perRepMap.set(r.requested_by, (perRepMap.get(r.requested_by) ?? 0) + 1);
  const perCatMap = new Map<OpsCategory, number>();
  for (const r of filed30) perCatMap.set(r.category, (perCatMap.get(r.category) ?? 0) + 1);
  return {
    open: open.length,
    urgent: open.filter((r) => r.priority === "urgent").length,
    overdue: open.filter((r) => agingTone(r, now) === "red").length,
    oldestDays: open.length ? Math.max(...open.map((r) => daysOpen(r, now))) : null,
    filed30: filed30.length,
    closed30: closed30.length,
    avgDaysToClose30: avg == null ? null : Math.round(avg * 10) / 10,
    perRep: [...perRepMap.entries()].map(([id, count]) => ({ id, count })).sort((a, b) => b.count - a.count),
    perCategory: [...perCatMap.entries()].map(([key, count]) => ({ key, count })).sort((a, b) => b.count - a.count),
  };
}
